import { FAVORITE_TYPES } from '../../domain/UserAccount/UserAccount.constants'
import { computeBBoxFromCoordinates } from '../../domain/utils/map'
import { createCustomSelector } from '../../domain/utils/react-reselect'
import { isFranceCountryCode } from '../../routes/address/Countries'
import { isEmpty } from '../../utils/lang'
import {
  getPoiApplicationId,
  getPoiCoordinates,
  getPoiCurrentTabId,
  getPoiIllustrationUrl,
  getPoiRubricId,
  getPoiRubricParent,
  getPoiRubricSegment
} from './geoentity.accessors'

export const selectGeoentityState = store => store?.geoentity ?? {}

export const selectAllGeoentities = createCustomSelector(selectGeoentityState, ({ pois = [] }) => pois)

export const selectGeoentities = createCustomSelector(selectAllGeoentities, pois => pois.filter(({ vde }) => !vde))

export const selectVdeGeoentities = createCustomSelector(selectAllGeoentities, pois =>
  pois.filter(({ vde }) => Boolean(vde))
)

export const selectCurrentGeoentityId = createCustomSelector(selectGeoentityState, ({ currentPoiId }) => currentPoiId)

const geoentityByIdCombiner = (geoentities = [], id) => geoentities.find(geoentity => geoentity.id === id)

export const selectGeoentityById = createCustomSelector(
  selectAllGeoentities,
  (_, props) => props?.id,
  geoentityByIdCombiner
)

export const selectCurrentGeoentity = createCustomSelector(
  selectAllGeoentities,
  selectCurrentGeoentityId,
  geoentityByIdCombiner
)

export const selectFirstGeoentity = createCustomSelector(selectGeoentities, geoentities => geoentities?.[0])

export const selectCurrentGeoentityAsFavorite = createCustomSelector(selectCurrentGeoentity, currentGeoentity => {
  if (!currentGeoentity) return null
  const { id, label, addressLabel } = currentGeoentity
  return {
    type: FAVORITE_TYPES.OTHER,
    poiId: id,
    label,
    address: addressLabel
  }
})

export const selectCurrentGeoentityRubricId = createCustomSelector(selectCurrentGeoentity, geoentity =>
  getPoiRubricId(geoentity)
)
export const selectCurrentGeoentityRubricSegment = createCustomSelector(selectCurrentGeoentity, geoentity =>
  getPoiRubricSegment(geoentity)
)
export const selectCurrentGeoentityParentRubricId = createCustomSelector(selectCurrentGeoentity, geoentity =>
  getPoiRubricParent(geoentity)
)

export const selectCurrentGeoentityCoordinates = createCustomSelector(selectCurrentGeoentity, geoentity =>
  getPoiCoordinates(geoentity)
)

const BBOX_SIZE_IN_METERS_AROUND_POI = 250

export const selectCurrentGeoentityComputedBbox = createCustomSelector(selectCurrentGeoentityCoordinates, point =>
  point.lng && point.lat ? computeBBoxFromCoordinates(BBOX_SIZE_IN_METERS_AROUND_POI)(point) : null
)

export const selectCurrentGeoentityIdentityCard = createCustomSelector(selectCurrentGeoentity, geoentity => {
  if (!geoentity) return
  const type = geoentity?.rubric?.label
  const icon = geoentity?.rubric?.icon
  const rubricId = geoentity?.rubric?.id
  const isRating = rubricId === 'hotel' || rubricId === 'camping'
  const rating = isRating && '*******'.substring(0, geoentity?.additionalInfo?.rating ?? 0)

  return {
    name: geoentity.name,
    address: geoentity.addressLabel,
    type,
    icon,
    rating,
    rubricId
  }
})

export const selectCurrentGeoentityTransportInfo = createCustomSelector(
  selectCurrentGeoentity,
  geoentity => geoentity?.transportInfo
)

export const selectCurrentGeoentityCurrentTab = createCustomSelector(selectCurrentGeoentity, geoentity => {
  if (!geoentity) return
  const { currentTabId, tabs = [] } = geoentity
  return tabs.find(({ appId }) => currentTabId === appId)
})

export const selectCurrentGeoentityCurrentTabId = createCustomSelector(selectCurrentGeoentity, getPoiCurrentTabId)

export const selectCurrentGeoentityTabContent = createCustomSelector(
  selectCurrentGeoentity,
  selectCurrentGeoentityCurrentTab,
  (geoentity, tab) => {
    const currentBlocksIdx = geoentity?.currentBlocksIdx ?? 0
    return tab?.content?.[currentBlocksIdx] ?? {}
  }
)

export const selectCurrentGeoentityBlocksContext = createCustomSelector(
  selectCurrentGeoentityTabContent,
  content => content.context
)

export const selectCurrentGeoentityBlocks = createCustomSelector(
  selectCurrentGeoentityTabContent,
  content => content?.blocks ?? []
)

export const selectHasCurrentGeoentityTabBlocks = createCustomSelector(
  selectCurrentGeoentity,
  selectCurrentGeoentityCurrentTab,
  selectCurrentGeoentityBlocks,
  (geoentity, tab, blocks) => {
    if ((geoentity?.tabs ?? []).length === 0) return false
    if (tab?.content) return !isEmpty(blocks)
    return true
  }
)

export const selectCurrentGeoentityTabBlocksCount = createCustomSelector(
  selectCurrentGeoentityBlocks,
  blocks => blocks.length
)

export const selectOtherGeoentities = createCustomSelector(
  selectGeoentities,
  selectCurrentGeoentityId,
  (pois, currentPoiId) => pois.filter(({ id }) => id !== currentPoiId)
)

export const selectFirstGeoentitySatisfying = createCustomSelector(
  selectGeoentities,
  (_, props) => props?.keys,
  (pois, keys) =>
    pois.find(poi => {
      const poiKeys = Object.keys(poi)
      return keys.every(key => poiKeys.includes(key))
    })
)

export const selectCurrentGeoentityLabelForUrl = createCustomSelector(selectCurrentGeoentity, geoentity => {
  if (!geoentity) return null
  const { name, way, postcode, town } = geoentity
  return `${name} ${way} ${postcode} ${town}`
})

export const selectCurrentGeoentityApplicationId = createCustomSelector(selectCurrentGeoentity, geoentity =>
  getPoiApplicationId(geoentity)
)

export const selectCurrentGeoentityPJId = createCustomSelector(selectCurrentGeoentity, geoentity => geoentity?.pjId)

export const selectCurrentGeoentityIsPJ = createCustomSelector(selectCurrentGeoentity, geoentity =>
  ['pj', 'pagesjaunes'].includes(geoentity?.prov)
)

export const selectCurrentGeoentityTabHasMultipleBlocks = createCustomSelector(
  selectCurrentGeoentityCurrentTab,
  tab => (tab?.content ?? []).length > 1
)

export const selectCurrentGeoentityTabBlocksTitles = createCustomSelector(selectCurrentGeoentityCurrentTab, tab =>
  (tab?.content ?? []).map(({ title }) => title)
)

export const selectCurrentGeoentityIllustrationUrl = createCustomSelector(selectCurrentGeoentity, getPoiIllustrationUrl)

export const selectHasOnlyCurrentGeoentity = createCustomSelector(
  selectGeoentities,
  selectCurrentGeoentityId,
  (geoentities, currentId) => geoentities.length === 1 && geoentities[0].id === currentId
)

export const selectHasGeoentities = createCustomSelector(selectGeoentities, geoentities => geoentities.length > 0)

export const selectHasVdeGeoentities = createCustomSelector(selectVdeGeoentities, geoentities => geoentities.length > 0)

export const selectFocusedPoiId = createCustomSelector(
  selectGeoentityState,
  geoentityState => geoentityState?.focusedPoiId
)

export const selectFocusedPoi = createCustomSelector(
  selectAllGeoentities,
  selectFocusedPoiId,
  (geoentities, focusedPoiId) => geoentities.find(({ id }) => focusedPoiId === id)
)

export const selectFocusedPoiIllustrationUrl = createCustomSelector(selectFocusedPoi, getPoiIllustrationUrl)

export const selectCurrentGeoentityHasFullContent = createCustomSelector(selectCurrentGeoentity, geoentity =>
  (geoentity?.tabs?.length ?? 0) > 0 ? geoentity.tabs.some(tab => !isEmpty(tab.content)) : true
)

export const selectIsWaitingForRequestedGeoentities = createCustomSelector(
  selectGeoentityState,
  geoentityState => geoentityState?.isWaitingForRequestedGeoentities
)

export const selectGeoentityViewport = createCustomSelector(
  selectGeoentityState,
  geoentityState => geoentityState?.viewport
)

export const selectIsGeoentityExtendBbox = createCustomSelector(
  selectGeoentityState,
  geoentityState => geoentityState?.extendBbox ?? false
)

export const selectGeoentityError = createCustomSelector(selectGeoentityState, ({ error }) => error)

export const selectCurrentGeoentityIsFrench = createCustomSelector(selectCurrentGeoentity, geoentity =>
  isFranceCountryCode(geoentity?.countryCode)
)

export const selectVisitedPoiIds = createCustomSelector(selectGeoentityState, state => state.visited ?? [])

export const selectContentWidget = createCustomSelector(selectGeoentityState, state => state.contentWidget)
